/**
 * iter84.59.13 — Re-pull Upstream Winners card.
 *
 * One-shot heal for tournaments where locally-stored `matches.winner_id`
 * disagrees with the upstream provider's `winner` boolean. Symptom:
 * leaderboards stuck for crews who drafted teams that advanced via
 * PK shootout BEFORE the iter84.59.12 trust-upstream refactor shipped.
 *
 * Mechanics:
 *  - Reads from the API-Football cache (zero quota burn).
 *  - Walks every played `matches` row for the tournament.
 *  - Maps the cached `winner_external_id` → canonical local team id
 *    using the same comparison logic as the live poller.
 *  - Updates `matches.winner_id` if it disagrees, then auto-runs
 *    `recompute_team_stats` so `team.stage_reached` / `eliminated`
 *    rebuild.
 *  - iter86.89 (WC retirement): API-Football-only — ESPN-bound
 *    tournaments are pointed at the ESPN sync repair card.
 *
 * Idempotent: tournaments already in sync return `winners_changed=0`
 * and skip the recompute step entirely.
 */
import { useState } from "react";
import { CloudDownload, AlertTriangle, CheckCircle2, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function SiteAdminRepullUpstreamWinnersCard() {
    const [tournamentId, setTournamentId] = useState("");
    const [busy, setBusy] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    const handleRun = async (dryRun) => {
        const tid = tournamentId.trim();
        if (!tid) {
            toast.error("Paste a tournament ID first.");
            return;
        }
        if (!dryRun) {
            const ok = window.confirm(
                `Re-pull winners from upstream + recompute team stats for tournament ${tid}?\n\n` +
                `This corrects any KO match where the local winner_id disagrees with ` +
                `what upstream (API-Football) says. Reads from the cache — ` +
                `no extra API quota burn.\n\n` +
                `Idempotent: tournaments already in sync are a no-op.`,
            );
            if (!ok) return;
        }
        setBusy(true);
        setLastResult(null);
        try {
            const res = await api.siteRepullUpstreamWinners(tid, dryRun);
            setLastResult(res);
            const action = dryRun ? "Preview" : "Heal";
            toast.success(
                `${action} complete (${res.source}): scanned ${res.matches_scanned} match(es), ` +
                `${res.winners_changed} winner(s) ${dryRun ? "would be corrected" : "corrected"}.`,
            );
        } catch (e) {
            const msg = e?.response?.data?.detail || "Re-pull failed";
            toast.error(msg);
            setLastResult({ error: msg });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="brutal-card p-4 space-y-3" data-testid="site-repull-upstream-winners-card">
            <div className="flex items-start gap-2">
                <CloudDownload className="w-4 h-4 text-volt shrink-0 mt-0.5" />
                <div className="flex-1">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-100">
                        Re-pull Upstream Winners
                    </h3>
                    <p className="text-xs text-zinc-400 mt-1">
                        Heals every KO match whose local
                        {" "}<code className="text-[10px] bg-black/40 px-1 rounded">winner_id</code>{" "}
                        disagrees with the upstream provider's
                        {" "}<code className="text-[10px] bg-black/40 px-1 rounded">winner</code>{" "}
                        boolean. Reads from the cache so no API quota is burned. Auto-runs
                        Recompute Team Stats afterward. Use when a PK shootout result didn't
                        propagate to the leaderboard.
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[200px]">
                    <label className="text-[10px] text-zinc-500 uppercase tracking-wider">
                        Tournament ID
                    </label>
                    <input
                        type="text"
                        value={tournamentId}
                        onChange={(e) => setTournamentId(e.target.value)}
                        placeholder="Paste tournament_id"
                        className="w-full mt-1 bg-black/40 border border-zinc-700 text-xs text-zinc-300 px-2 py-1.5 rounded"
                        data-testid="site-repull-upstream-winners-tournament-input"
                    />
                </div>
                <button
                    type="button"
                    onClick={() => handleRun(true)}
                    disabled={busy || !tournamentId.trim()}
                    className="brutal-button px-3 py-2 text-xs uppercase tracking-wider flex items-center gap-1"
                    data-testid="site-repull-upstream-winners-dry-run-btn"
                    title="Preview what would change without writing"
                >
                    <FlaskConical className="w-3 h-3" />
                    {busy ? "Running…" : "Dry-run"}
                </button>
                <button
                    type="button"
                    onClick={() => handleRun(false)}
                    disabled={busy || !tournamentId.trim()}
                    className="brutal-button px-3 py-2 text-xs uppercase tracking-wider bg-volt/20 border-volt"
                    data-testid="site-repull-upstream-winners-run-btn"
                >
                    {busy ? "Healing…" : "Re-pull now"}
                </button>
            </div>

            {lastResult?.error && (
                <div className="text-xs text-rose-400 flex items-center gap-1" data-testid="site-repull-upstream-winners-error">
                    <AlertTriangle className="w-3 h-3" /> {lastResult.error}
                </div>
            )}

            {lastResult && !lastResult.error && (
                <div
                    className="text-xs space-y-1 bg-zinc-900/40 border border-zinc-700/40 rounded p-2"
                    data-testid="site-repull-upstream-winners-result"
                >
                    <div className="flex items-center gap-2 text-emerald-300">
                        <CheckCircle2 className="w-3 h-3" />
                        <strong className="uppercase tracking-wider">
                            {lastResult.dry_run ? "Dry-run preview" : "Heal applied"}
                            <span className="ml-2 text-[10px] text-zinc-500 normal-case">
                                source={lastResult.source}
                            </span>
                        </strong>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-zinc-300 pl-5">
                        <div>Matches scanned: <span className="font-mono text-zinc-100">{lastResult.matches_scanned}</span></div>
                        <div>Winners corrected: <span className="font-mono text-zinc-100">{lastResult.winners_changed}</span></div>
                        <div>From null → set: <span className="font-mono text-zinc-100">{lastResult.winners_set_from_none}</span></div>
                        <div>Skipped (no upstream): <span className="font-mono text-zinc-100">{lastResult.skipped_no_upstream}</span></div>
                        {lastResult.recompute_summary && (
                            <div className="col-span-2 text-emerald-300">
                                Recompute: {lastResult.recompute_summary.teams_recomputed} team(s) rebuilt from
                                {" "}{lastResult.recompute_summary.matches_surviving} match(es).
                            </div>
                        )}
                    </div>
                    {lastResult.dry_run && (
                        <p className="text-[10px] text-amber-300 italic pl-5 mt-1">
                            Dry-run only — nothing was written. Click "Re-pull now" to apply.
                        </p>
                    )}
                    {lastResult.fixture_id_misses?.length > 0 && (
                        <p className="text-[10px] text-zinc-500 italic pl-5 mt-1" data-testid="site-repull-upstream-winners-misses">
                            {lastResult.fixture_id_misses.length} fixture id(s) missing from cache (showing first 20): {lastResult.fixture_id_misses.slice(0, 20).join(", ")}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
